import { useState, useEffect, useCallback, useMemo } from 'react';
import rendicionService from '../../services/rendicionService';
import ImportPolizasModal from './ImportPolizasModal';
import DetalleRendicionModal from './DetalleRendicionModal';
import { formatDate, toISODateChile, todayISOChile } from '../../styles/tokens/formatters';

/** Identidad de un lote: compañía + tipo + día. Sin el día, dos lotes de la
 *  misma compañía compartían estado de "enviando" y se bloqueaban entre sí. */
const loteKey = (l) => `${l.compania_id}-${l.tipo_rendicion}-${l.periodo ?? ''}`;

// ── CSS mirroring the design ──────────────────────────────────────────────────
const RD_STYLES = `
  .rd-lote { all: unset; box-sizing: border-box; cursor: pointer; display: flex; width: 100%; border-bottom: 1px solid #F1F1EF; background: #fff; transition: background .12s ease; }
  .rd-lote:hover { background: #FAFAFA; }
  .rd-lote .rd-bar { width: 3px; flex-shrink: 0; background: transparent; }
  .rd-lote.active, .rd-lote.active:hover { background: #F4FBFA; }
  .rd-lote.active .rd-bar { background: #007D6E; }

  .rd-tipo { font-size: 10.5px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; border-radius: 5px; padding: 2px 7px; }
  .rd-tipo.colectiva  { color: #1D4ED8; background: #EAF1FF; }
  .rd-tipo.individual { color: #7E22CE; background: #F3EBFF; }

  .rd-stage { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; }
  .rd-stage .rd-sdot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
  .rd-stage.generar { color: #8A5A0B; }
  .rd-stage.enviado { color: #0369A1; }
  .rd-stage.done    { color: #0A7C46; }

  .rd-box { width: 18px; height: 18px; border-radius: 5px; border: 1.5px solid #CFCFCD; background: #fff; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 11px; font-weight: 700; flex-shrink: 0; cursor: pointer; }
  .rd-box.on   { border-color: #007D6E; background: #007D6E; }
  .rd-box.some { border-color: #007D6E; background: #9BD0C9; color: #007D6E; }

  .rd-row { background: #fff; border-bottom: 1px solid #F3F3F2; cursor: pointer; transition: background .1s ease; }
  .rd-row:hover { background: #FAFAF9; }
  .rd-row.sel   { background: #F4FBFA; }

  .rd-pill { display: inline-flex; align-items: center; gap: 6px; font-size: 11.5px; font-weight: 600; border-radius: 20px; padding: 3px 10px; white-space: nowrap; }
  .rd-pill .rd-pdot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; }
  .rd-pill.sinproceso { color: #6B6F72; background: #ECECEA; }
  .rd-pill.pendiente  { color: #8A5A0B; background: #FBF1CC; }
  .rd-pill.recibido   { color: #0A7C46; background: #E1F1E7; }
  .rd-pill.error      { color: #B91C1C; background: #FEE2E2; }

  .rd-step { display: flex; align-items: center; flex: 1; min-width: 0; }
  .rd-step.last { flex: 0 0 auto; }
  .rd-step .rd-scircle { width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; background: #F1F1EF; color: #B4B4B2; border: 1.5px solid #E0E0DE; }
  .rd-step .rd-sk { font-size: 9.5px; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: #B4B4B2; margin: 0; }
  .rd-step .rd-sl { font-size: 13px; font-weight: 600; color: #A0A09E; margin: 2px 0 0; }
  .rd-step .rd-sline { flex: 1; height: 2px; margin: 0 14px; border-radius: 2px; background: #E4E4E2; min-width: 20px; }
  .rd-step.last .rd-sline { display: none; }
  .rd-step.done    .rd-scircle { background: #007D6E; color: #fff; border-color: #007D6E; }
  .rd-step.current .rd-scircle { background: #fff; color: #007D6E; border-color: #007D6E; }
  .rd-step.done    .rd-sk, .rd-step.current .rd-sk { color: #007D6E; }
  .rd-step.done    .rd-sl, .rd-step.current .rd-sl { color: #1A1A1A; }
  .rd-step.done    .rd-sline { background: #007D6E; }

  @keyframes rd-rise { from { opacity: 0; transform: translate(-50%, 14px); } to { opacity: 1; transform: translate(-50%, 0); } }
`;

// ── Helpers ───────────────────────────────────────────────────────────────────
function stageLabel(stage, tipo) {
  if (stage === 'generar') return 'Por generar';
  if (stage === 'done')    return 'Rendido';
  return tipo === 'colectiva' ? 'En espera de acuse' : 'En espera recepción';
}

const ESTADO_PILL = {
  null:                { cls: 'sinproceso', label: 'Sin proceso' },
  pendiente_recepcion: { cls: 'pendiente',  label: 'Pdte. recepción' },
  error_envio:         { cls: 'error',      label: 'Error envío' },
  emitido:             { cls: 'recibido',   label: 'Emitido' },
};

const HIST_BADGE = {
  pendiente:  { bg: '#F1F1EF', color: '#6B6F72', label: 'Pendiente' },
  generada:   { bg: '#FBF1CC', color: '#8A5A0B', label: 'Generada' },
  enviada:    { bg: '#DBEEFF', color: '#0369A1', label: 'Enviada' },
  respondida: { bg: '#E1F1E7', color: '#0A7C46', label: 'Respondida' },
  error:      { bg: '#FEE2E2', color: '#B91C1C', label: 'Error' },
};

// Ambos tipos se generan y envían igual. Después difieren:
//   colectiva  → registrar recepción (acuse: certs pasan a 'emitido')
//   individual → recibir las pólizas emitidas por la compañía (CSV con numero_poliza)
// La etapa del lote depende de si existe una rendición abierta (generada/enviada).
function findRendicionAbierta(g, historial) {
  return historial.find(h =>
    h.compania_id === g.compania_id &&
    (h.tipo_rendicion || 'individual') === g.tipo_rendicion &&
    ['generada', 'enviada'].includes(h.estado)
  ) ?? null;
}

function getLoteStage(g, historial) {
  if (g.certificados.some(c => c.estado_emision === 'error_envio')) return 'generar';
  return findRendicionAbierta(g, historial) ? 'enviado' : 'generar';
}

function getMonogram(name) {
  const s = (name ?? '').trim();
  const w = s.split(/\s+/);
  return w.length >= 2 && w[0] && w[1] ? (w[0][0] + w[1][0]).toUpperCase() : s.slice(0, 2).toUpperCase() || '?';
}

// ── KPI Card ──────────────────────────────────────────────────────────────────
function KPICard({ label, value, sub, alert: isAlert, accent }) {
  const valColor = isAlert ? '#8A5A0B'
    : accent === 'green' ? '#0A7C46'
    : accent === 'cyan'  ? '#0369A1'
    : '#1A1A1A';
  return (
    <div style={{
      background: isAlert ? '#FEF9EC' : '#fff',
      border: `1px solid ${isAlert ? '#F7D97B' : '#E6E6E4'}`,
      borderRadius: 14, padding: '18px 20px',
    }}>
      <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#8D8D8D', margin: '0 0 14px' }}>
        {label}
      </p>
      <p style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-.04em', color: valColor, margin: 0, lineHeight: 1 }}>
        {value}
      </p>
      <p style={{ fontSize: 12, color: '#8D8D8D', margin: '6px 0 0' }}>{sub}</p>
    </div>
  );
}

// ── Stepper ───────────────────────────────────────────────────────────────────
function Stepper({ stage, tipo }) {
  const stageNum = stage === 'generar' ? 0 : stage === 'enviado' ? 1 : 3;
  const paso3 = tipo === 'colectiva' ? 'Recepción registrada' : 'Pólizas recibidas';
  const steps = [{ k: 'Paso 1', l: 'Generado' }, { k: 'Paso 2', l: 'Enviado' }, { k: 'Paso 3', l: paso3 }];
  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {steps.map((s, i) => {
        const done = i < stageNum, current = i === stageNum, last = i === 2;
        const cls = `rd-step${done ? ' done' : current ? ' current' : ''}${last ? ' last' : ''}`;
        return (
          <div key={i} className={cls}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <span className="rd-scircle">{done ? '✓' : i + 1}</span>
              <div style={{ lineHeight: 1.2 }}>
                <p className="rd-sk">{s.k}</p>
                <p className="rd-sl">{s.l}</p>
              </div>
            </div>
            {!last && <span className="rd-sline" />}
          </div>
        );
      })}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function RendicionesPage() {
  const [pendientes, setPendientes] = useState([]);
  const [historial,  setHistorial]  = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [tab,        setTab]        = useState('pend');
  const [activeKey,  setActiveKey]  = useState(null);
  const [selected,   setSelected]   = useState({});
  const [searchQ,    setSearchQ]    = useState('');
  const [sending,    setSending]    = useState({});
  const [errMsg,     setErrMsg]     = useState(null);
  const [importTarget, setImportTarget] = useState(null); // rendición (fila de historial) a importar
  const [detalleTarget, setDetalleTarget] = useState(null); // rendición a mostrar en detalle cert↔póliza

  // Refresco silencioso: el "Cargando…" de página completa solo aplica a la carga
  // inicial (loading parte en true); refrescar durante un modal abierto no lo desmonta.
  const cargar = useCallback(async () => {
    setErrMsg(null);
    try {
      const [p, h] = await Promise.all([
        rendicionService.getPendientesPorCompania(),
        rendicionService.getHistorial(),
      ]);
      setPendientes(p.data ?? []);
      setHistorial(h.data ?? []);
    } catch (err) {
      setErrMsg(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const queue = useMemo(() => {
    const q = searchQ.toLowerCase();
    return pendientes.filter(g => !q || (g.compania_nombre || '').toLowerCase().includes(q));
  }, [pendientes, searchQ]);

  const activeLote = useMemo(() => {
    const found = queue.find(g => loteKey(g) === activeKey);
    return found ?? queue[0] ?? null;
  }, [queue, activeKey]);

  const currentKey    = activeLote ? loteKey(activeLote) : null;
  const activeStage   = activeLote ? getLoteStage(activeLote, historial) : 'done';
  const activeAbierta = activeLote ? findRendicionAbierta(activeLote, historial) : null;

  const selCount   = useMemo(() => Object.values(selected).filter(Boolean).length, [selected]);
  const totalCerts = activeLote?.certificados?.length ?? 0;
  const allSel     = totalCerts > 0 && activeLote?.certificados?.every(c => selected[c.id]);
  const someSel    = !allSel && activeLote?.certificados?.some(c => selected[c.id]);

  // KPIs
  const kTotal   = pendientes.reduce((n, g) => n + g.certificados.length, 0);
  const kGenerar = pendientes.reduce((n, g) =>
    getLoteStage(g, historial) === 'generar' ? n + g.certificados.length : n, 0);
  const kEspera  = pendientes.reduce((n, g) =>
    getLoteStage(g, historial) === 'enviado' ? n + g.certificados.length : n, 0);
  const mesActual = todayISOChile().slice(0, 7);
  const kRendidos = historial
    .filter(h => h.estado === 'respondida' && toISODateChile(h.created_at).startsWith(mesActual))
    .reduce((n, h) => n + (h.total_certificados ?? 0), 0);

  // ── Handlers ─────────────────────────────────────────────────────────────
  async function handleGenerar(lote) {
    const key = loteKey(lote);
    // El período es el DÍA DEL LOTE, no "hoy". Con `todayISOChile()` la rendición
    // se etiquetaba con la fecha de generación aunque contuviera certificados de
    // otro día — de ahí la del 2026-07-13 con certificados de agosto.
    // La colectiva no tiene día (su unidad es la póliza), así que ahí sí va hoy.
    const periodo = lote.periodo ?? todayISOChile();
    setSending(s => ({ ...s, [key]: true }));
    try {
      const r = await rendicionService.generarYEnviar(lote.compania_id, periodo, lote.tipo_rendicion);
      const metodo = r.metodo === 'manual' ? 'generada (descarga manual)' : `enviada vía ${r.metodo}`;
      alert(`Rendición creada: ${r.total_certificados} certificados, ${metodo}`);
      setTab('hist');
      await cargar();
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setSending(s => ({ ...s, [key]: false }));
    }
  }

  // Un `<a download>` guarda lo que venga, incluido un error del servidor: así
  // terminó un JSON en Descargas ("Sin certificados pendientes de rendir para
  // esta compañía") con el id de la rendición por nombre, y la pantalla sin decir
  // nada. Un ancla no puede mirar el status; por eso va por fetch.
  async function handleDescargarCSV(r) {
    try {
      await rendicionService.descargarArchivo(
        rendicionService.getDescargaUrl(r.id),
        r.archivo_nombre ?? `rendicion_${r.id}.csv`
      );
    } catch (err) {
      alert(`No se pudo descargar el CSV: ${err.message}`);
    }
  }

  async function handleAcuseRecibo(rendicionId) {
    if (!window.confirm('¿Confirmar acuse de recibo? Los certificados colectivos pendientes quedarán marcados como emitidos.')) return;
    try {
      const r = await rendicionService.acuseRecibo(rendicionId);
      alert(`Acuse registrado: ${r.ok} certificados emitidos`);
      await cargar();
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  }

  function toggleCert(id) {
    setSelected(s => ({ ...s, [id]: !s[id] }));
  }

  function toggleAll() {
    if (!activeLote) return;
    if (allSel) {
      setSelected({});
    } else {
      const next = {};
      activeLote.certificados.forEach(c => { next[c.id] = true; });
      setSelected(next);
    }
  }

  function pickLote(key) { setActiveKey(key); setSelected({}); }

  // Acción según etapa y tipo:
  //   generar             → Generar y enviar (ambos tipos)
  //   enviado + colectiva → Registrar recepción (acuse de recibo)
  //   enviado + individual→ Recibir pólizas emitidas (cargar CSV de la compañía)
  function ActionControl() {
    if (!activeLote || activeStage === 'done') return null;
    const busy = !!sending[currentKey];
    const baseStyle = { padding: '8px 18px', background: busy ? '#9BD0C9' : '#007D6E', color: '#fff', fontSize: 13, fontWeight: 600, borderRadius: 9, border: 'none', cursor: busy ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0, transition: 'background .12s' };

    if (activeStage === 'generar') {
      return (
        <button disabled={busy} onClick={() => handleGenerar(activeLote)} style={baseStyle}>
          {busy ? 'Procesando…' : <>Generar y enviar <span>→</span></>}
        </button>
      );
    }
    if (activeLote.tipo_rendicion === 'colectiva') {
      return (
        <button disabled={busy || !activeAbierta} onClick={() => handleAcuseRecibo(activeAbierta.id)} style={baseStyle}>
          Registrar recepción <span>→</span>
        </button>
      );
    }
    return (
      <button
        disabled={busy || !activeAbierta}
        onClick={() => setImportTarget(activeAbierta)}
        style={baseStyle}
      >
        Recibir pólizas emitidas <span>→</span>
      </button>
    );
  }

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240, color: '#A0A09E', fontSize: 13 }}>
      Cargando…
    </div>
  );

  return (
    <div style={{ padding: '30px 30px 140px', maxWidth: 1440, width: '100%', fontFamily: "var(--font-sans,'Inter',system-ui,sans-serif)" }}>
      <style>{RD_STYLES}</style>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div>
          <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', color: '#007D6E', margin: 0 }}>
            Seguros · {new Date().toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })}
          </p>
          <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: '-.035em', margin: '8px 0 0', color: '#1A1A1A' }}>
            Rendición de pólizas
          </h1>
          <p style={{ fontSize: 14, color: '#54585A', margin: '8px 0 0', lineHeight: 1.5, maxWidth: 560 }}>
            Genera los certificados, envíalos a las aseguradoras y registra la recepción desde un solo flujo.
          </p>
        </div>
      </div>

      {errMsg && (
        <div style={{ marginTop: 16, padding: '10px 14px', background: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 10, fontSize: 13, color: '#B91C1C' }}>
          {errMsg}
        </div>
      )}

      {/* ── KPIs ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginTop: 26 }}>
        <KPICard label="Certificados por rendir" value={kTotal}    sub={`en ${pendientes.length} lotes`} />
        <KPICard label="Por generar y enviar"     value={kGenerar}  sub="requieren acción"                alert={kGenerar > 0} />
        <KPICard label="En espera de recepción"   value={kEspera}   sub="enviados a la aseguradora"       accent="cyan" />
        <KPICard label="Rendidos este mes"         value={kRendidos} sub="certificados completados"        accent="green" />
      </div>

      {/* ── Tabs ── */}
      <div style={{ marginTop: 26, borderBottom: '1px solid #E6E6E4', display: 'flex' }}>
        {[['pend', 'Pendientes de rendir', queue.length], ['hist', 'Historial', historial.length]].map(([key, label, count]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            all: 'unset', cursor: 'pointer', padding: '10px 16px', fontSize: 13, fontWeight: 500,
            borderBottom: tab === key ? '2px solid #007D6E' : '2px solid transparent',
            color: tab === key ? '#007D6E' : '#54585A',
            marginBottom: -1, transition: 'color .12s',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            {label}
            {count > 0 && (
              <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 20, padding: '1px 8px', background: tab === key ? '#007D6E' : '#E4E4E2', color: tab === key ? '#fff' : '#6B6F72' }}>
                {count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── PENDIENTES: master-detail ── */}
      {tab === 'pend' && (
        <div style={{ display: 'grid', gridTemplateColumns: '352px 1fr', gap: 20, alignItems: 'start', marginTop: 22 }}>

          {/* Cola de lotes */}
          <div style={{ background: '#fff', border: '1px solid #E6E6E4', borderRadius: 16, overflow: 'hidden', position: 'sticky', top: 78 }}>
            <div style={{ padding: '16px 16px 12px', borderBottom: '1px solid #EFEFEE' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.13em', textTransform: 'uppercase', color: '#8D8D8D', margin: 0 }}>
                  Lotes por rendir
                </p>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#54585A', background: '#F1F1EF', borderRadius: 20, padding: '2px 9px' }}>
                  {queue.length}
                </span>
              </div>
              <input
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                placeholder="Buscar aseguradora…"
                style={{ marginTop: 12, width: '100%', padding: '8px 12px', fontSize: 13, border: '1px solid #E0E0DE', borderRadius: 8, outline: 'none', background: '#FAFAF9', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ maxHeight: 560, overflowY: 'auto' }}>
              {queue.length === 0 ? (
                <p style={{ padding: '34px 20px', textAlign: 'center', fontSize: 13, color: '#8D8D8D', margin: 0 }}>
                  {pendientes.length === 0 ? 'Sin lotes pendientes ✓' : 'Sin coincidencias'}
                </p>
              ) : queue.map(g => {
                const key   = loteKey(g);
                const stage = getLoteStage(g, historial);
                return (
                  <button key={key} onClick={() => pickLote(key)} className={`rd-lote${key === currentKey ? ' active' : ''}`}>
                    <span className="rd-bar" />
                    <span style={{ flex: 1, minWidth: 0, padding: '14px 16px' }}>
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#1A1A1A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {g.compania_nombre}
                        </span>
                        <span style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, color: '#54585A' }}>
                          {g.certificados.length} cert.
                        </span>
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 9 }}>
                        <span className={`rd-tipo ${g.tipo_rendicion}`}>
                          {g.tipo_rendicion === 'colectiva' ? 'Colectiva' : 'Individual'}
                        </span>
                        <span className={`rd-stage ${stage}`}>
                          <span className="rd-sdot" />{stageLabel(stage, g.tipo_rendicion)}
                        </span>
                        {/* El día del lote. Sin esto, dos lotes de la misma
                            compañía se ven idénticos en la lista. */}
                        {g.periodo && (
                          <span style={{ fontSize: 11, color: '#6B6F72', whiteSpace: 'nowrap' }}>
                            {formatDate(g.periodo)}
                          </span>
                        )}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Detalle del lote */}
          {activeLote ? (
            <div style={{ background: '#fff', border: '1px solid #E6E6E4', borderRadius: 16, overflow: 'hidden' }}>

              {/* Cabecera */}
              <div style={{ padding: '22px 24px', borderBottom: '1px solid #EFEFEE', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 9, background: '#0E1116', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, flexShrink: 0 }}>
                    {getMonogram(activeLote.compania_nombre)}
                  </div>
                  <div>
                    <h2 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-.02em', margin: 0 }}>
                      {activeLote.compania_nombre ?? <span style={{ color: '#A0A09E', fontStyle: 'italic' }}>Sin compañía asignada</span>}
                    </h2>
                    <p style={{ fontSize: 12.5, color: '#8D8D8D', margin: '3px 0 0' }}>
                      Rendición {activeLote.tipo_rendicion} · {activeLote.certificados.length} certificados
                    </p>
                  </div>
                </div>
                <ActionControl />
              </div>

              {/* Stepper */}
              <div style={{ padding: '20px 24px', borderBottom: '1px solid #EFEFEE', background: '#FBFBFA' }}>
                <Stepper stage={activeStage} tipo={activeLote.tipo_rendicion} />
              </div>

              {/* Toolbar seleccionar todo */}
              <div style={{ display: 'grid', gridTemplateColumns: '44px 160px 1fr 210px', alignItems: 'center', padding: '11px 24px', borderBottom: '1px solid #EFEFEE', background: '#FAFAF9' }}>
                <button onClick={toggleAll} style={{ all: 'unset', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span className={`rd-box${allSel ? ' on' : someSel ? ' some' : ''}`} style={{ color: someSel && !allSel ? '#007D6E' : undefined }}>
                    {allSel ? '✓' : someSel ? '–' : ''}
                  </span>
                </button>
                {['Código', 'Cliente', 'Estado de emisión'].map(h => (
                  <span key={h} style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: '#A0A09E' }}>{h}</span>
                ))}
              </div>

              {/* Filas de certificados */}
              <div>
                {activeLote.certificados.map(c => {
                  // Colectivos viven con estado_emision NULL hasta el acuse:
                  // antes de enviar → 'Por rendir'; enviada la rendición → 'Pdte. acuse recibo'.
                  const pill = c.estado_emision
                    ? (ESTADO_PILL[c.estado_emision] ?? ESTADO_PILL[null])
                    : activeLote.tipo_rendicion === 'colectiva'
                      ? (activeStage === 'enviado'
                          ? { cls: 'pendiente',  label: 'Pdte. acuse recibo' }
                          : { cls: 'sinproceso', label: 'Por rendir' })
                      : ESTADO_PILL[null];
                  const isSel = !!selected[c.id];
                  return (
                    <div
                      key={c.id}
                      onClick={() => toggleCert(c.id)}
                      className={`rd-row${isSel ? ' sel' : ''}`}
                      style={{ display: 'grid', gridTemplateColumns: '44px 160px 1fr 210px', alignItems: 'center', padding: '13px 24px' }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span className={`rd-box${isSel ? ' on' : ''}`}>{isSel ? '✓' : ''}</span>
                      </span>
                      <span style={{ fontFamily: 'ui-monospace,monospace', fontSize: 12.5, color: '#54585A' }}>
                        {c.codigo}
                      </span>
                      <span style={{ fontSize: 13.5, fontWeight: 500, color: '#1A1A1A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', paddingRight: 16 }}>
                        {c.cliente_nombre}
                      </span>
                      <span>
                        <span className={`rd-pill ${pill.cls}`}>
                          <span className="rd-pdot" />{pill.label}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #E6E6E4', borderRadius: 16, padding: '64px 40px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: '#E1F1E7', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24 }}>✓</div>
              <h2 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-.02em', margin: '18px 0 0' }}>Todo rendido</h2>
              <p style={{ fontSize: 14, color: '#8D8D8D', margin: '8px 0 0', maxWidth: 340, lineHeight: 1.5 }}>
                No quedan lotes pendientes. Revisa el historial para ver los envíos anteriores.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── HISTORIAL ── */}
      {tab === 'hist' && (
        <div style={{ marginTop: 22 }}>
          <div style={{ background: '#fff', border: '1px solid #E6E6E4', borderRadius: 16, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#FAFAF9', borderBottom: '1px solid #EFEFEE' }}>
                  {['Fecha', 'Compañía', 'Tipo', 'Período', 'Certs.', 'Recibidos', 'Estado', 'Acciones'].map(h => (
                    <th key={h} style={{ padding: '12px 20px', textAlign: 'left', fontSize: 10, fontWeight: 700, letterSpacing: '.09em', textTransform: 'uppercase', color: '#A0A09E', whiteSpace: 'nowrap' }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {historial.map(r => {
                  const badge       = HIST_BADGE[r.estado] ?? HIST_BADGE.pendiente;
                  const companyName = r.companias?.nombre_corto ?? (r.compania_id?.slice(0, 8) + '…');
                  return (
                    <tr key={r.id} style={{ borderBottom: '1px solid #F3F3F2' }}>
                      <td style={{ padding: '14px 20px', color: '#8D8D8D', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {formatDate(r.created_at)}
                      </td>
                      <td style={{ padding: '14px 20px' }}>
                        <p style={{ margin: 0, fontWeight: 600, color: '#1A1A1A', fontSize: 13 }}>{companyName}</p>
                      </td>
                      <td style={{ padding: '14px 20px' }}>
                        <span className={`rd-tipo ${r.tipo_rendicion || 'individual'}`}>
                          {r.tipo_rendicion === 'colectiva' ? 'Colectiva' : 'Individual'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 20px', color: '#54585A', fontFamily: 'ui-monospace,monospace', fontSize: 12 }}>
                        {r.periodo}
                      </td>
                      <td style={{ padding: '14px 20px', color: '#1A1A1A', fontWeight: 600, textAlign: 'right' }}>
                        {r.total_certificados ?? '—'}
                      </td>
                      <td style={{ padding: '14px 20px', color: r.total_polizas_recibidas > 0 ? '#0A7C46' : '#A0A09E', textAlign: 'right' }}>
                        {r.total_polizas_recibidas > 0 ? r.total_polizas_recibidas : '—'}
                      </td>
                      <td style={{ padding: '14px 20px' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, fontWeight: 600, borderRadius: 20, padding: '3px 10px', background: badge.bg, color: badge.color, whiteSpace: 'nowrap' }}>
                          <span style={{ width: 5, height: 5, borderRadius: '50%', background: badge.color, flexShrink: 0 }} />
                          {badge.label}
                        </span>
                      </td>
                      <td style={{ padding: '14px 20px' }}>
                        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                          <button
                            onClick={() => handleDescargarCSV(r)}
                            style={{ all: 'unset', cursor: 'pointer', fontSize: 12, color: '#007D6E', textDecoration: 'underline', whiteSpace: 'nowrap' }}
                          >
                            CSV
                          </button>
                          {r.estado !== 'respondida' && r.tipo_rendicion === 'colectiva' && (
                            <button onClick={() => handleAcuseRecibo(r.id)} style={{ all: 'unset', cursor: 'pointer', fontSize: 12, color: '#0A7C46', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
                              Acuse de recibo
                            </button>
                          )}
                          {r.estado !== 'respondida' && r.tipo_rendicion !== 'colectiva' && (
                            <button onClick={() => setImportTarget(r)} style={{ all: 'unset', cursor: 'pointer', fontSize: 12, color: '#0A7C46', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
                              Cargar respuesta
                            </button>
                          )}
                          {r.tipo_rendicion !== 'colectiva' && (
                            <button onClick={() => setDetalleTarget(r)} style={{ all: 'unset', cursor: 'pointer', fontSize: 12, color: '#0369A1', textDecoration: 'underline', whiteSpace: 'nowrap' }}>
                              Detalle
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {!historial.length && (
                  <tr>
                    <td colSpan={8} style={{ padding: '56px 20px', textAlign: 'center', color: '#A0A09E', fontSize: 13 }}>
                      Sin historial de rendiciones
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Barra flotante (selección activa) ── */}
      {selCount > 0 && (
        <div style={{
          position: 'fixed', bottom: 26, left: '50%', transform: 'translateX(-50%)',
          zIndex: 50, display: 'flex', alignItems: 'center', gap: 18,
          padding: '11px 12px 11px 20px', background: '#0E1116', borderRadius: 14,
          boxShadow: '0 18px 44px rgba(14,17,22,.34)', animation: 'rd-rise .18s ease both', whiteSpace: 'nowrap',
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#fff', fontSize: 13.5, fontWeight: 600 }}>
            <span style={{ minWidth: 24, height: 24, padding: '0 7px', borderRadius: 8, background: '#EC4899', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>
              {selCount}
            </span>
            {selCount === 1 ? 'certificado seleccionado' : 'certificados seleccionados'}
          </span>
          <span style={{ width: 1, height: 24, background: 'rgba(255,255,255,.14)' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button onClick={() => setSelected({})} style={{ all: 'unset', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,.65)', padding: '8px 12px', borderRadius: 9 }}>
              Limpiar
            </button>
            <ActionControl />
          </div>
        </div>
      )}

      {importTarget && (
        <ImportPolizasModal
          rendicion={importTarget}
          onClose={() => setImportTarget(null)}
          onImported={cargar}
        />
      )}

      {detalleTarget && (
        <DetalleRendicionModal
          rendicion={detalleTarget}
          onClose={() => setDetalleTarget(null)}
        />
      )}
    </div>
  );
}
